import React, { useEffect, useState } from 'react';
import { useAdminStore } from '@/store/adminStore';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageCircle, CheckCircle, XCircle, Clock, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useI18n } from '@/components/I18nProvider';

const statusConfig = {
  sent: { label: 'Gönderildi', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  failed: { label: 'Başarısız', icon: XCircle, color: 'bg-red-100 text-red-700' },
  pending: { label: 'Bekliyor', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
};

export default function AdminMessagesPage() {
  const { t } = useI18n();
  const { messages, isLoading, fetchMessages, deleteMessage, deleteAllMessages } = useAdminStore();
  const [deletingId, setDeletingId] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    const res = await deleteMessage(id);
    if (res.success) toast.success('Mesaj silindi');
    else toast.error(res.error || 'Silinemedi');
    setDeletingId(null);
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    const res = await deleteAllMessages();
    if (res.success) toast.success(res.message || 'Tüm mesajlar silindi');
    else toast.error(res.error || 'Silinemedi');
    setDeletingAll(false);
  };

  if (isLoading && messages.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="admin-messages-page">
        <h1 className="font-heading text-3xl font-bold">Mesaj Logları</h1>
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Mesaj Logları</h1>
          <p className="mt-1 text-muted-foreground">Sistem genelinde gönderilen mesajlar ({messages.length})</p>
        </div>
        {messages.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" data-testid="delete-all-messages-btn">
                <Trash2 className="mr-2 h-4 w-4" />
                Tümünü Sil
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Tüm mesaj loglarını sil?</AlertDialogTitle>
                <AlertDialogDescription>
                  Bu işlem {messages.length} mesaj logunu kalıcı olarak silecektir. Bu işlem geri alınamaz.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteAll} disabled={deletingAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-all-btn">
                  {deletingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                  Evet, Tümünü Sil
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="messages-table">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground sm:px-6">Firma</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground sm:px-6">{t('phone')}</th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left font-medium text-muted-foreground">Tip</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground sm:px-6">{t('status')}</th>
                  <th className="hidden md:table-cell px-6 py-3 text-left font-medium text-muted-foreground">Tarih</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground sm:px-6">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((m) => {
                  const sc = statusConfig[m.status] || statusConfig.pending;
                  return (
                    <tr key={m.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`message-row-${m.id}`}>
                      <td className="px-4 py-4 font-medium sm:px-6">{m.company_name || '-'}</td>
                      <td className="px-4 py-4 text-muted-foreground sm:px-6">{m.phone}</td>
                      <td className="hidden sm:table-cell px-6 py-4">
                        <Badge variant="outline">{m.message_type}</Badge>
                      </td>
                      <td className="px-4 py-4 sm:px-6">
                        <Badge className={sc.color}><sc.icon className="mr-1 h-3 w-3" />{sc.label}</Badge>
                      </td>
                      <td className="hidden md:table-cell px-6 py-4 text-muted-foreground">
                        {new Date(m.created_at).toLocaleString('tr-TR')}
                      </td>
                      <td className="px-4 py-4 text-right sm:px-6">
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => handleDelete(m.id)}
                          disabled={deletingId === m.id}
                          data-testid={`delete-message-${m.id}`}
                        >
                          {deletingId === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-destructive" />}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {messages.length === 0 && (
                  <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">
                    <MessageCircle className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                    Henüz mesaj kaydı yok
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
